import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { captureException } from "@sentry/react";
import differenceBy from "lodash/differenceBy";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  applyErrorsToFormFields,
  findEntities,
  findEntity,
  getApiErrors,
} from "~api/helpers";
import { useManagers } from "~api/managers.ts";
import { useDeleteTeam, useUpdateTeam } from "~api/teams";
import { Dialog } from "~components/shared/Dialog/Dialog";
import { ContentSkeleton } from "~components/shared/Loading/ContentSkeleton/ContentSkeleton";
import { useToast } from "~components/shared/useToast.tsx";
import { useTranslation } from "~contexts/I18nContext/I18nContext.tsx";
import { useResources } from "~contexts/ResourceContext/ResourceContext.tsx";
import type { Resource as APIResource } from "~generated";
import type { Resource } from "~lib/resourceHelpers.ts";
import { ResourceType } from "~lib/resourceHelpers.ts";
import styles from "./EditTeamModal.module.scss";
import type { EditTeamModalData } from "./EditTeamModalContext";
import type { TeamFormData } from "./TeamForm";
import { TeamForm } from "./TeamForm";

export interface EditTeamModalProps {
  isModalOpen: boolean;
  onClose: () => void;
  data: EditTeamModalData | null;
}

const getIdsByType = (resources: Resource[], type: APIResource.type) =>
  resources.filter((r) => r.type === type).map((r) => r.id);

export function EditTeamModal({
  isModalOpen,
  onClose,
  data,
}: EditTeamModalProps) {
  const team = data?.team;
  const additionalResources = data?.additionalResources;
  const onSuccess = data?.onSuccess;
  const updateTeamMutation = useUpdateTeam();
  const deleteTeamMutation = useDeleteTeam();
  const {
    activeResources: availableResources,
    isLoading: areResourcesLoading,
  } = useResources();
  const {
    data: constructionSiteManagers,
    isLoading: areConstructionSiteManagersLoading,
  } = useManagers();
  const t = useTranslation();
  const { showErrorToast } = useToast();
  const [isFormReady, setIsFormReady] = useState(false);

  const handleFormReady = useCallback(
    (isReady: boolean) =>
      setIsFormReady(
        isReady && !areResourcesLoading && !areConstructionSiteManagersLoading,
      ),
    [areResourcesLoading, areConstructionSiteManagersLoading],
  );

  const defaultValues = useMemo(() => {
    const resources = findEntities(availableResources, [
      ...(team?.member_ids || []),
      ...(team?.tool_ids || []),
    ]);

    return {
      name: team?.name,
      resources,
      foreman: findEntity(availableResources, team?.foreman_id),
      constructionSiteManager: findEntity(
        constructionSiteManagers,
        team?.construction_site_manager_id,
      ),
    };
  }, [availableResources, constructionSiteManagers, team]);

  const formMethods = useForm<TeamFormData>({
    defaultValues,
    mode: "onBlur",
  });
  const { setError, reset, getValues, handleSubmit } = formMethods;

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const close = () => {
    reset();
    setIsFormReady(false);
    onClose();
  };

  const onSubmit = async ({
    name,
    resources,
    constructionSiteManager,
    foreman,
  }: TeamFormData) => {
    updateTeamMutation.mutate(
      {
        id: team?.id,
        name: name.trim(),
        construction_site_manager_id: constructionSiteManager?.id,
        foreman_id: foreman?.id,
        member_ids: getIdsByType(resources, ResourceType.EMPLOYEE),
        tool_ids: getIdsByType(resources, ResourceType.TOOL),
      },
      {
        onSuccess: () => {
          if (onSuccess) {
            const [added, removed] = createDiff({
              original: defaultValues.resources,
              changed: resources,
            });
            onSuccess?.(added, removed);
          }
          close();
        },
        onError: (error) => {
          try {
            applyErrorsToFormFields(getApiErrors(error), setError, getValues());
          } catch (e) {
            showErrorToast(t.unknownError);
            captureException(e);
          }
        },
      },
    );
  };

  const onDelete = async () => {
    deleteTeamMutation.mutate(team?.id as string, {
      onSuccess: close,
      onError: (error) => {
        try {
          applyErrorsToFormFields(getApiErrors(error), setError, getValues());
        } catch (e) {
          showErrorToast(t.unknownError);
          throw e;
        }
      },
    });
  };

  return (
    <Dialog
      dialogTitle={t.editTeam}
      open={isModalOpen}
      onClose={close}
      fullWidth={true}
    >
      {areResourcesLoading ? (
        <ContentSkeleton withPadding contentType="dialog" />
      ) : (
        <form
          onSubmit={(event) => {
            event.stopPropagation();
            return handleSubmit(onSubmit)(event);
          }}
        >
          <DialogContent>
            <FormProvider {...formMethods}>
              <TeamForm
                team={team}
                additionalResources={additionalResources}
                onFormReady={handleFormReady}
              />
            </FormProvider>
          </DialogContent>
          <DialogActions>
            {team && (
              <Button
                color="error"
                variant="contained"
                onClick={onDelete}
                className={styles.deleteBtn}
              >
                {t.teamFormDelete}
              </Button>
            )}
            <Button onClick={close}>{t.teamFormCancel}</Button>
            <Button
              loading={updateTeamMutation.isPending}
              type="submit"
              disabled={!isFormReady || updateTeamMutation.isPending}
              variant="contained"
            >
              {t.teamFormSave}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}

function createDiff({
  original,
  changed,
}: {
  original: Resource[];
  changed: Resource[];
}): [Resource[], Resource[]] {
  const added = differenceBy(changed, original, "id");
  const removed = differenceBy(original, changed, "id");
  return [added, removed];
}
